import { makeVar } from '@apollo/client'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import {
    DocumentTemplateView,
    DocumentTemplateViewItem,
} from '@deltek/specification-client/core/Models/documentTemplate'
import { Nugget } from '@deltek/specification-client/core/Models/nugget'
import { ProductFamily } from '@deltek/specification-client/core/Models/productFamily'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProductTypeProperty } from '@deltek/specification-client/core/Models/productTypeProperty'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { ProjectManufacturerProductDetail } from '@deltek/specification-client/core/Models/projectManufacturerProductDetail'
import { ProjectNugget } from '@deltek/specification-client/core/Models/projectNugget'
import {
    WorkspaceTabTitles,
    mergeArrayOnSpecificIndex,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import {
    getTabName,
    getUpdatedItemsArray,
} from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceContentArea'
import {
    GetNuggetResponse,
    GetProductFamilyResponse,
    GetProjectNuggetResponse,
    GetProjectProductFamilyResponse,
    ImportedManufacturersAndBOD,
    ProjectManufacturerProductDetailOnFamily,
} from '../types/projectElement.types'

import { ArchitectEngineeringFamilyIteration } from 'models/projectElementNode'
import { NormalizedChildDocumentTemplateViewItem } from 'pages/ProjectsPage/WorkspacePage/components/Common/model'
import { createReactiveVarStore } from './createReactiveVarStore'
import { workspaceProperty } from './property'

export interface TableCellTextContent {
    id: string
    type: string
    text: string
    metricUnits: string
    imperialUnits: string
    elements: TableCellTextContent[]
}

export interface TableCell {
    cellPoints: number
    columnNumber: number
    rowNumber: number
    content: string
    isMergedCell: boolean
    columnNumberEnd: number
    columnNumberStart: number
    textContentElements: TableCellTextContent[]
}
export interface TableList {
    id: string
    tableIdentifier: string
    totalColumns: number
    totalRows: number
    tableHeader: TableCell[]
    content: TableCell[]
}

export interface ProductFamilyWithTable extends ProductFamily {
    tableList: Partial<TableList>[]
}

export interface UpdateWorkspaceContentParams {
    updatedDtv?: Partial<DocumentTemplateView>
    mergeDtv?: boolean
    contentItems?: Partial<ContentItem>[]
    isAddProhibited?: boolean
    isAddOngoing?: boolean
    productListings?: Partial<ProjectManufacturerProductDetail>[]
}
interface LastElementCursorIndexIdMap {
    [key: string]: number
}
export interface WorkspaceContent {
    productTypeProperties: Partial<ProductTypeProperty>[]
    contentItemNodes?: Partial<ContentItem>[]
    tableList?: Partial<TableList>[]
    dtv: Partial<DocumentTemplateView> | null
    projectProductFamilyId?: string
    productListings?: Partial<ProjectManufacturerProductDetail>[]
    nuggetId?: string
    isAddProhibited?: boolean
    isAddOngoing?: boolean
    tabSelected: number
    resetOtherTabs?: boolean
    isNewAddOnQueue?: boolean
    addQueueSib?: string[] | undefined
    elementIdOnEdit?: string | undefined
    isCancelAddDialogOpen?: boolean
    lastElementCursorIndexIds?: LastElementCursorIndexIdMap
    familyAddedProductTypes?: ProjectElementNode[]
    familyProductListings?: Partial<ProjectManufacturerProductDetailOnFamily>[]
    familyCustomManufacturerProducts?: Partial<
        ProjectManufacturerProductDetailOnFamily
    >[]
    familyViewImportedManufaturerListing?: Partial<
        ImportedManufacturersAndBOD
    >[]
    familyViewImportedBODListing?: Partial<ImportedManufacturersAndBOD>[]
    isManufacturerAdded?: boolean
    customManufacturerProducts?: Partial<ProjectManufacturerProductDetail>[]
    isDraftMode?: boolean
    isWorkspaceHasError?: boolean
    isProjectImported?: boolean
    isRemoveNuggetGqlOngoing?: boolean
    // True while node-scoped detail queries are in-flight; prevents remove racing active fetches
    isNuggetDetailsFetching?: boolean
    // Separate from isNuggetDetailsFetching — owned by useProductFetcher, not WorkspaceContentArea
    isNuggetContentViewFetching?: boolean
    availableVersions?: ArchitectEngineeringFamilyIteration[]
}

export const workspaceContentInitial: WorkspaceContent = {
    productTypeProperties: [],
    isWorkspaceHasError: false,
    contentItemNodes: [],
    tableList: [],
    dtv: {},
    isAddProhibited: false,
    isAddOngoing: false,
    tabSelected: 0,
    resetOtherTabs: false,
    isDraftMode: false,
    isProjectImported: false,
    isRemoveNuggetGqlOngoing: false,
    isNuggetDetailsFetching: false,
    isNuggetContentViewFetching: false,
    availableVersions: [],
}
export interface UpdateProjectContent {
    documentTemplateView: Partial<DocumentTemplateView>
    projectNuggets: Partial<ProjectNugget>[]
    projectElementId: string
    contentItemNodes?: Partial<ContentItem>[]
}
export interface UpdateProjectContentsforPill {
    setProjectContentItemElements: Partial<UpdateProjectContent>
}

// TODO: migrate to `createReactiveVarStore` for optimal updates. Do a whole project reference search then rename all at once.
/**
 * @deprecated This function has been deprecated for mutating workspace content.
 * Consider using `setReactiveWorkspaceContents` for optimal updates.
 */
export const workspaceContents = makeVar<WorkspaceContent>(
    workspaceContentInitial
)

export const [
    reactiveWorkspaceContents,
    setReactiveWorkspaceContents,
] = createReactiveVarStore<WorkspaceContent>(workspaceContentInitial)

/**
 * Mutate workspace content by merging new content into the current state.
 * @param {Partial<WorkspaceContent>} newWorkspaceContent - New content.
 */
export const setWorkspaceContents = (
    newWorkspaceContent: Partial<WorkspaceContent>
) => {
    // TODO: replace deprecated `workspaceContents` function with `setReactiveWorkspaceContents`
    // Merge new content into the current workspace
    const updatedContents = { ...workspaceContents(), ...newWorkspaceContent }
    workspaceContents(updatedContents)
    // updating reactive var as well to get prompt update
    setReactiveWorkspaceContents(updatedContents)
}

export const isContentTeam = makeVar(false) // Only true for Deltek users
export const isOmWorkspaceUser = makeVar(false) // Only true for all AE users
export const isElementPublished = makeVar(false)
export const isWorkspaceEditable = makeVar(false)
export const workspaceHasError = makeVar(false)

export interface PromoteDemoteTemporaryParagraphInterface {
    content?: NormalizedChildDocumentTemplateViewItem
    action?: string
    contentIdTrigger?: string
}

export const promoteDemoteTemporaryParagraphData = makeVar<
    PromoteDemoteTemporaryParagraphInterface | null | undefined
>(null)
export const updatePromoteDemoteTemporaryParagraphData = (
    newData: PromoteDemoteTemporaryParagraphInterface | null
) => {
    promoteDemoteTemporaryParagraphData(newData)
}

export const contentTeamProjectId = makeVar('DraftMasterSpecProject')

export const setWorkspacePTPs = (
    nugget?: Partial<ProjectNugget>[] | Partial<Nugget>[],
    ptpList?: Partial<ProductTypeProperty>[],
    selectedElementType?: ProjectElementType,
    productListings?: Partial<ProjectManufacturerProductDetail>[]
) => {
    let currentWorkspaceContents = workspaceContents()

    if (productListings) {
        currentWorkspaceContents = {
            ...currentWorkspaceContents,
            productListings,
        }
    }

    if (selectedElementType === ProjectElementType.ASSEMBLY) {
        setWorkspaceContents({
            productTypeProperties: [],
        })
    } else if (selectedElementType === ProjectElementType.PRODUCTFAMILY) {
        setWorkspaceContents({
            productTypeProperties:
                ptpList ?? ([] as Partial<ProductTypeProperty>[]),
        })
    } else {
        setWorkspaceContents({
            productTypeProperties:
                (nugget?.[0] as Partial<ProjectNugget>)
                    ?.projectProductTypeProperties ??
                ((nugget?.[0] as Partial<Nugget>)
                    ?.productTypeProperties as Partial<ProductTypeProperty>[]),
            productListings: productListings
                ? productListings
                : currentWorkspaceContents.productListings,
        })
    }
}

export const setWorkspaceHasError = (value: boolean) => {
    workspaceHasError(value)
}

export const resetOtherTabs = (value: boolean) => {
    setWorkspaceContents({ resetOtherTabs: value })
}

export const resetWorkspaceContents = () => {
    setWorkspaceContents(workspaceContentInitial)
}

export const setWorkspaceContentItems = (
    contentItems?: Partial<ContentItem>[]
): Promise<void> => {
    setWorkspaceContents({
        contentItemNodes: contentItems,
    })
    return Promise.resolve()
}

export const setWorkspaceContentItemsViaNugget = (
    nugget?: GetProjectNuggetResponse | GetNuggetResponse,
    familyData?: GetProductFamilyResponse | GetProjectProductFamilyResponse
) => {
    const contentItemNodes = nugget
        ? (
              (nugget as GetProjectNuggetResponse)?.getProjectNugget ??
              (nugget as GetNuggetResponse)?.nugget
          )?.contentItemNodes
        : (
              (familyData as GetProductFamilyResponse).productFamily ??
              (familyData as GetProjectProductFamilyResponse)
                  .getProjectProductFamily
          )?.contentItemNodes
    const tableList = (
        ((familyData as GetProductFamilyResponse)?.productFamily as Partial<
            ProductFamilyWithTable
        >) ??
        (familyData as GetProjectProductFamilyResponse)?.getProjectProductFamily
    )?.tableList!

    setWorkspaceContents({
        contentItemNodes: contentItemNodes,
        tableList: tableList,
    })
}

export const setWorkspaceDTV = (
    dtv?: Partial<DocumentTemplateView> | null,
    productListings?: Partial<ProjectManufacturerProductDetail>[]
) => {
    const {
        dtv: oldDtv,
        productListings: oldProductListings,
        isAddProhibited,
        isAddOngoing,
    } = workspaceContents()

    setWorkspaceContents({
        dtv: dtv ? dtv : dtv === null ? null : oldDtv,
        productListings: productListings ?? oldProductListings,
        projectProductFamilyId: dtv?.productFamilyId,
        isAddProhibited: dtv === null ? false : isAddProhibited,
        isAddOngoing: dtv === null ? false : isAddOngoing,
    })
}

/** Updates multiple workspaceContents fields at once to avoid excessive re-renders */
export const updateWorkspaceContents = (
    params: UpdateWorkspaceContentParams
) => {
    const {
        dtv: oldDtv,
        productListings: oldProductListings,
        isAddProhibited: oldIsAddProhibited,
        isAddOngoing: oldIsAddOngoing,
        contentItemNodes: oldContentItemNodes,
    } = workspaceContents()
    const {
        updatedDtv: newDtv,
        mergeDtv: mergeDtv,
        productListings: newProductListings,
        isAddProhibited: newIsAddProhibited,
        isAddOngoing: newIsAddOngoing,
        contentItems: newContentItemNodes,
    } = params
    const dtv = newDtv ? (mergeDtv ? getMergedDtv(newDtv) : newDtv) : oldDtv

    setWorkspaceContents({
        dtv: dtv ? dtv : dtv === null ? null : oldDtv,
        productListings: newProductListings ?? oldProductListings,
        projectProductFamilyId: dtv?.productFamilyId,
        isAddProhibited:
            dtv === null
                ? false
                : newIsAddProhibited === undefined
                ? oldIsAddProhibited
                : newIsAddProhibited,
        isAddOngoing:
            dtv === null
                ? false
                : newIsAddOngoing === undefined
                ? oldIsAddOngoing
                : newIsAddOngoing,
        contentItemNodes: newContentItemNodes ?? oldContentItemNodes,
    })
}

export const setUpdatedDtv = async (
    updatedDtv: Partial<DocumentTemplateView>,
    oldDtvItems?: Partial<DocumentTemplateViewItem>[]
) => {
    const mergedDtv = getMergedDtv(updatedDtv, oldDtvItems)
    setWorkspaceDTV(mergedDtv)
}

const getMergedDtv = (
    updatedDtv: Partial<DocumentTemplateView>,
    oldDtvItems?: Partial<DocumentTemplateViewItem>[] | undefined
) => {
    const { dtv: existingDTV } = workspaceContents()
    const mergedDtv =
        existingDTV && existingDTV?.id === updatedDtv?.id
            ? {
                  ...updatedDtv,
                  items: getUpdatedItemsArray(
                      oldDtvItems ?? existingDTV?.items ?? [],
                      updatedDtv?.items ?? [],
                      false
                  ),
              }
            : updatedDtv
    return mergedDtv
}

//update dtv items from specific part, retain items from parts
export const updatePartDTV = (
    updatedDTV: DocumentTemplateView,
    dtv: Partial<DocumentTemplateView>,
    isStudioManagement?: boolean
) => {
    const isSustainabilityTopic = updatedDTV?.name?.includes('Sustainablity')
    const deletedPropertyPart: string = updatedDTV?.items?.some(
        (dtvItem: Partial<DocumentTemplateViewItem>) =>
            dtvItem.partType === 'General'
    )
        ? 'General'
        : updatedDTV?.items?.some(
              (dtvItem: Partial<DocumentTemplateViewItem>) =>
                  dtvItem.partType === 'Products'
          )
        ? 'Products'
        : 'Execution'

    const { isAcceptAllRejectAll } = workspaceProperty()
    const { tabSelected } = workspaceContents()

    const updatedDtvFromAcceptAllRejectAll = isAcceptAllRejectAll
        ? {
              ...dtv,
              items: dtv.items?.filter(
                  (item) =>
                      item.partType === WorkspaceTabTitles[tabSelected ?? 0]
              ),
          }
        : dtv

    if (!!isStudioManagement || !!isSustainabilityTopic) {
        setWorkspaceDTV({
            ...updatedDTV,
        })
    } else {
        setWorkspaceDTV({
            ...updatedDTV,
            items: [
                ...((
                    (updatedDtvFromAcceptAllRejectAll?.items?.filter(
                        (dtvItem: Partial<DocumentTemplateViewItem>) =>
                            dtvItem.partType !== deletedPropertyPart
                    ) as Partial<DocumentTemplateViewItem>[]) ?? []
                ).concat(
                    updatedDTV?.items as Partial<DocumentTemplateViewItem>[]
                ) as Partial<DocumentTemplateViewItem>[]),
            ],
        })
    }
}

export const handleDTVReorder = (
    productTypeTab: number,
    reorderedDtv: DocumentTemplateView
) => {
    const tabName = getTabName(productTypeTab)
    const oldItems = workspaceContents()?.dtv?.items
    const reOrderItems = reorderedDtv.items?.filter(
        (item) => item.partType === tabName
    )
    const indexforNewData = oldItems?.findIndex(
        (item) => item.partType === tabName
    )
    const filterItems = oldItems?.filter((item) => item.partType !== tabName)
    const newOrderItems = mergeArrayOnSpecificIndex(
        filterItems,
        reOrderItems,
        indexforNewData
    )

    setWorkspaceDTV({
        ...reorderedDtv,
        items: newOrderItems,
        productFamilyId:
            reorderedDtv.productFamilyId ??
            workspaceContents().projectProductFamilyId,
    } as Partial<DocumentTemplateView>)
}

export const handleDTVMFRandBODUpdate = () => {
    const existingDTV = workspaceContents().dtv
    setWorkspaceDTV({
        ...existingDTV,
        items: existingDTV?.items?.map((dtvItem) => {
            if (dtvItem.isBOD) {
                return {
                    ...dtvItem,
                    documentTemplateViewContents: dtvItem?.documentTemplateViewContents?.map(
                        (dtvContent) => ({
                            ...dtvContent,
                            documentTemplateViewContentItems: dtvContent.documentTemplateViewContentItems?.map(
                                (dtvContentItem) => ({
                                    ...dtvContentItem,
                                    isHidden:
                                        dtvContentItem.order !== 0
                                            ? !dtvContentItem?.isHidden
                                            : dtvContentItem?.isHidden,
                                })
                            ),
                        })
                    ),
                }
            } else if (dtvItem.isManufacturers) {
                return {
                    ...dtvItem,
                    documentTemplateViewContents: dtvItem?.documentTemplateViewContents?.map(
                        (dtvContent) => ({
                            ...dtvContent,
                            documentTemplateViewContentItems: dtvContent.documentTemplateViewContentItems?.map(
                                (dtvContentItem) => ({
                                    ...dtvContentItem,
                                    isHidden: !dtvContentItem?.isHidden,
                                })
                            ),
                        })
                    ),
                }
            } else {
                return dtvItem
            }
        }),
    })
}

export const setIsWorkspaceEditable = (value: boolean) => {
    isWorkspaceEditable(value)
}

export const setIsAddProhibited = (value: boolean) => {
    setWorkspaceContents({ isAddProhibited: value })
}

export const setIsAddOngoing = (value: boolean) => {
    setWorkspaceContents({ isAddOngoing: value })
}

export const setIsAddOnQueue = (value: boolean) => {
    setWorkspaceContents({ isNewAddOnQueue: value })
}
export const setAddQueueSib = (
    contentItemId: string | undefined,
    isPreviousToPop: boolean = false
) => {
    const currentContent = workspaceContents()
    const addQueue =
        currentContent.addQueueSib && currentContent.addQueueSib?.length > 0
            ? currentContent.addQueueSib
            : []
    //empty string is an indication that add is triggered on an ongoing add
    const actualAddQueue = isPreviousToPop
        ? addQueue?.slice(0, addQueue?.findIndex((id) => id && id === '') + 1)
        : addQueue

    setWorkspaceContents({
        addQueueSib:
            typeof contentItemId === 'string' && contentItemId
                ? Array.from(new Set(actualAddQueue.concat([contentItemId]))) //to ensure no duplicates
                : undefined,
    })
}

export const setElementIdOnEdit = (value: string | undefined) => {
    setWorkspaceContents({ elementIdOnEdit: value })
}
export const setWorkspaceTab = (value: number) => {
    setWorkspaceContents({ tabSelected: value })
}
export const setIsCancelAddDialogOpen = (value: boolean) => {
    setWorkspaceContents({ isCancelAddDialogOpen: value })
}

export const updateLastElementIndexId = (value: {}) => {
    setWorkspaceContents({
        lastElementCursorIndexIds: value,
    })
}

export const setFamilyAddedProductTypeData = (
    productTypes?: ProjectElementNode[],
    productListing?: Partial<ProjectManufacturerProductDetailOnFamily>[],
    customManufacturerProducts?: Partial<
        ProjectManufacturerProductDetailOnFamily
    >[],
    manufacturerImportedManufacturer?: Partial<ImportedManufacturersAndBOD>[],
    bodImportedManufacturer?: Partial<ImportedManufacturersAndBOD>[]
) => {
    setFamilyAddedProductListing(
        productListing,
        customManufacturerProducts,
        manufacturerImportedManufacturer,
        bodImportedManufacturer
    )
    setWorkspaceContents({
        familyAddedProductTypes: productTypes || [],
    })
}

export const setFamilyAddedProductListing = (
    productListing?: Partial<ProjectManufacturerProductDetailOnFamily>[],
    customManufacturerProducts?: Partial<
        ProjectManufacturerProductDetailOnFamily
    >[],
    manufacturerImportedManufacturer?: Partial<ImportedManufacturersAndBOD>[],
    bodImportedManufacturer?: Partial<ImportedManufacturersAndBOD>[]
) => {
    setWorkspaceContents({
        familyProductListings: productListing || [],
        familyCustomManufacturerProducts: customManufacturerProducts || [],
        familyViewImportedManufaturerListing:
            manufacturerImportedManufacturer || [],
        familyViewImportedBODListing: bodImportedManufacturer || [],
    })
}
export const setIsManufacturerAdded = (value: boolean) => {
    setWorkspaceContents({ isManufacturerAdded: value })
}

export const setCustomManufacturerProducts = (
    value: Partial<ProjectManufacturerProductDetail>[]
) => {
    setWorkspaceContents({
        customManufacturerProducts: value,
    })
}

export const setFamilyAddedProductTypes = (value?: ProjectElementNode[]) => {
    setWorkspaceContents({
        familyAddedProductTypes: value,
    })
}

export interface ContentAreaLoading {
    isContentAreaLoading: boolean
}

const contentAreaLoadingInitial = {
    isContentAreaLoading: false,
}

export const contentAreaLoading = makeVar<ContentAreaLoading>({
    ...contentAreaLoadingInitial,
})

export const setContentAreaLoading = (value: boolean) => {
    contentAreaLoading({ isContentAreaLoading: value })
}

export const setIsRemoveNuggetGqlOngoing = (value: boolean) => {
    setWorkspaceContents({ isRemoveNuggetGqlOngoing: value })
}

export const setIsNuggetDetailsFetching = (value: boolean) => {
    setWorkspaceContents({ isNuggetDetailsFetching: value })
}

// Reference-counted so multiple useProductFetcher instances don't clear each other's in-flight state
let nuggetContentViewFetchCount = 0

export const beginNuggetContentViewFetch = () => {
    nuggetContentViewFetchCount += 1
    if (nuggetContentViewFetchCount === 1) {
        setWorkspaceContents({ isNuggetContentViewFetching: true })
    }
}

export const endNuggetContentViewFetch = () => {
    if (nuggetContentViewFetchCount === 0) return
    nuggetContentViewFetchCount -= 1
    if (nuggetContentViewFetchCount === 0) {
        setWorkspaceContents({ isNuggetContentViewFetching: false })
    }
}

// TODO: include this state & handler in workspaceContents reactive var, if possible
/**
 * Reactive variables to be used as a replacement for the deprecated useState hook from WorkspacePage and ProjectPage [contentTeamFromStudioPage, setContentTeamFromStudioPage].
 * this is to avoid the need to pass the state down through the component tree.
 *
 * @deprecated This reactive variable has been deprecated for new workspace. Use `useIsContentTeamFromStudioPage` from Workspace2Store instead.
 * @type {Object} isReactiveContentTeamFromStudioPage - The reactive variable.
 * @type {Function} setIsReactiveContentTeamFromStudioPage - The function to update the reactive variable.
 */
export const [
    isReactiveContentTeamFromStudioPage,
    setIsReactiveContentTeamFromStudioPage,
] = createReactiveVarStore({ value: false })
